import type { paths } from '@cou/contracts';
import { adminApi, type ApiFailure } from '../api/admin-api';

/*
 * The Awards API client (API-23, admin-ui.md → Awards). Serves the global Awards section, the
 * contest Awards tab, and the player detail's awards summary — one row shape for all three.
 */

export type AdminAward =
  paths['/api/admin/awards']['get']['responses'][200]['content']['application/json']['awards'][number];

type ListAwardsBody =
  paths['/api/admin/awards']['get']['responses'][200]['content']['application/json'];

export type ListAwardsResult = ({ ok: true } & ListAwardsBody) | ApiFailure;
export type RetryAwardResult = { ok: true; award: AdminAward } | ApiFailure;

/** The Award delivery states the list filters by (game-logic.md, the Award state table). */
export const AWARD_STATES = [
  'Pending',
  'Processing',
  'Delivered',
  'Retryable',
  'Failed',
  'Canceled',
] as const;

export type ListAwardsFilter = {
  readonly status?: string;
  readonly contestId?: string;
  readonly userId?: string;
  /** Account-ref search — a contains match (API-23). */
  readonly account?: string;
  readonly limit?: number;
  readonly cursor?: string;
};

/** Browse Awards, filterable by delivery state, Contest and player (API-23 GET). */
export async function listAwards(
  adminToken: string,
  filter: ListAwardsFilter,
): Promise<ListAwardsResult> {
  return adminApi.get<ListAwardsBody>(adminToken, '/api/admin/awards', filter);
}

/**
 * Return a Failed Award to Retryable with its attempts reset (API-23 POST, GL-13). A 409 means the
 * Award moved out of Failed under the operator — the screen refreshes rather than insisting.
 */
export async function retryAward(adminToken: string, id: string): Promise<RetryAwardResult> {
  const result = await adminApi.post<AdminAward>(adminToken, `/api/admin/awards/${id}/retry`);
  return result.ok ? { ok: true, award: result.body } : result;
}

export interface AwardsClient {
  listAwards(adminToken: string, filter: ListAwardsFilter): Promise<ListAwardsResult>;
  retryAward(adminToken: string, id: string): Promise<RetryAwardResult>;
}

export const defaultAwardsClient: AwardsClient = { listAwards, retryAward };
